import React, { useState, useEffect } from 'react';
import CreatorService from '../../../services/CreatorService';
import AuthService from '../../../services/AuthService';
import './BecomeCreatorPage.css';

const VITRINE_URL =
  process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_APP_URL || 'http://app.local.dev:8080';

const RitaaaBrand = () => (
  <a href={VITRINE_URL} className="ritaaa-brand" aria-label="Ritaaa — accueil">
    <span className="ritaaa-brand-name">Ritaaa</span>
    <span className="ritaaa-brand-mark" aria-hidden="true">
      <svg viewBox="0 0 36 36" className="ritaaa-brand-star">
        <path d="M18 4 L21.2 14.8 L32 18 L21.2 21.2 L18 32 L14.8 21.2 L4 18 L14.8 14.8 Z" />
      </svg>
    </span>
  </a>
);

const slugify = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const BecomeCreatorPage = () => {
  const [formData, setFormData] = useState({
    shopName: '',
    cguAccepted: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [error, setError] = useState('');
  const [suggestedSlugs, setSuggestedSlugs] = useState([]);
  const [showCgu, setShowCgu] = useState(false);

  const previewSlug = slugify(formData.shopName);

  const redirectToDashboard = () => {
    window.location.replace('/dashboard');
  };

  const hasCreatorRole = (authUser) => {
    if (!authUser?.roles) return false;
    return authUser.roles.some((userRole) => {
      if (typeof userRole === 'string') {
        return userRole.toLowerCase() === 'creator' || userRole.toLowerCase() === 'shop_owner';
      }

      if (userRole && typeof userRole === 'object') {
        const candidate = userRole.name ?? userRole.role ?? userRole.value;
        if (typeof candidate === 'string') {
          const normalizedCandidate = candidate.toLowerCase();
          return normalizedCandidate === 'creator' || normalizedCandidate === 'shop_owner';
        }
      }

      return false;
    });
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = await AuthService.checkAuthentication();
        if (result.authenticated) {
          if (hasCreatorRole(result.user)) {
            redirectToDashboard();
            return;
          }
        } else {
          AuthService.redirectToLogin(window.location.href);
        }
      } catch (error) {
        console.error('Erreur auth:', error);
        AuthService.redirectToLogin(window.location.href);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    if (name === 'shopName') {
      setSuggestedSlugs([]);
    }
  };

  const submitShop = async ({ shopName, shopSlug, cguAccepted }) => {
    const result = await CreatorService.createCreator(
      shopName,
      cguAccepted,
      shopSlug || null
    );

    if (result.success) {
      redirectToDashboard();
      return;
    }

    if (result.code === 'SHOP_SLUG_TAKEN') {
      setSuggestedSlugs(result.suggested_slugs || []);
    } else {
      setSuggestedSlugs([]);
    }

    setError(result.error || 'Une erreur est survenue');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.shopName.trim()) {
      setError('Le nom de la boutique est requis');
      setIsLoading(false);
      return;
    }

    if (formData.shopName.trim().length < 3) {
      setError('Le nom de la boutique doit contenir au moins 3 caractères');
      setIsLoading(false);
      return;
    }

    if (!formData.cguAccepted) {
      setError('Vous devez accepter les conditions générales d\'utilisation');
      setIsLoading(false);
      return;
    }

    try {
      await submitShop({
        shopName: formData.shopName.trim(),
        shopSlug: null,
        cguAccepted: formData.cguAccepted
      });
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = async (slug) => {
    setError('');
    setIsLoading(true);

    try {
      await submitShop({
        shopName: formData.shopName.trim(),
        shopSlug: slug,
        cguAccepted: formData.cguAccepted
      });
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="become-creator-page">
        <div className="become-creator-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="become-creator-page">
      <div className="become-creator-container">
        <div className="become-creator-header">
          <RitaaaBrand />
          <h1>Créer votre boutique</h1>
          <p className="subtitle">
            Pour vendre et participer avec Ritaaa. Page boutique, fiches produits,
            contact avec les visiteurs.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="become-creator-form">
          <div className="form-group">
            <label htmlFor="shopName">
              Nom de votre boutique
              <span className="required">*</span>
            </label>
            <input
              type="text"
              id="shopName"
              name="shopName"
              value={formData.shopName}
              onChange={handleChange}
              placeholder="Ex. Atelier des Collines"
              disabled={isLoading}
              autoFocus
            />
            <p className="input-hint">
              {previewSlug
                ? `Adresse publique : /boutiques/${previewSlug}`
                : 'Ce nom sera visible par vos clients et représentera votre marque.'}
            </p>
          </div>

          {suggestedSlugs.length > 0 && (
            <div className="slug-suggestions">
              <p>Adresses disponibles :</p>
              <div className="slug-suggestion-list">
                {suggestedSlugs.map((slug) => (
                  <button
                    key={slug}
                    type="button"
                    className="slug-suggestion"
                    disabled={isLoading}
                    onClick={() => handleSuggestionClick(slug)}
                  >
                    {slug}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group cgu-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="cguAccepted"
                checked={formData.cguAccepted}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">
                J'accepte les{' '}
                <button
                  type="button"
                  className="cgu-link"
                  onClick={() => setShowCgu(true)}
                >
                  Conditions Générales d'Utilisation
                </button>
                <span className="required">*</span>
              </span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Création en cours...
              </>
            ) : (
              'Créer ma boutique Ritaaa'
            )}
          </button>
        </form>

        <div className="info-cards">
          <div className="info-card">
            <span className="card-icon">💳</span>
            <h3>Sans engagement</h3>
            <p>Créez votre boutique gratuitement, sans frais cachés.</p>
          </div>
          <div className="info-card">
            <span className="card-icon">📦</span>
            <h3>Simple à utiliser</h3>
            <p>Ajoutez vos produits en quelques clics seulement.</p>
          </div>
          <div className="info-card">
            <span className="card-icon">📈</span>
            <h3>Développez votre activité</h3>
            <p>Touchez des milliers de clients potentiels.</p>
          </div>
        </div>
      </div>

      {showCgu && (
        <div className="modal-overlay" onClick={() => setShowCgu(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Conditions Générales d'Utilisation</h2>
              <button className="modal-close" onClick={() => setShowCgu(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <h3>1. Objet</h3>
              <p>
                Les présentes conditions générales d'utilisation régissent l'utilisation
                de la plateforme de vente en ligne. En créant une boutique, vous acceptez
                de vous conformer à ces conditions.
              </p>

              <h3>2. Inscription et compte</h3>
              <p>
                Pour créer une boutique, vous devez être une personne physique ou morale
                disposant de la capacité juridique pour contracter. Vous vous engagez à
                fournir des informations exactes et à jour.
              </p>

              <h3>3. Responsabilités du vendeur</h3>
              <p>
                En tant que vendeur, vous êtes responsable des produits que vous proposez
                à la vente, de leur description, de leur qualité et de leur conformité
                aux réglementations en vigueur.
              </p>

              <h3>4. Commission et paiements</h3>
              <p>
                Une commission sera prélevée sur chaque vente réalisée via la plateforme.
                Les modalités de paiement et les délais de versement sont détaillés dans
                votre espace vendeur.
              </p>

              <h3>5. Protection des données</h3>
              <p>
                Vos données personnelles sont traitées conformément à notre politique de
                confidentialité et au RGPD. Vous disposez d'un droit d'accès, de
                rectification et de suppression de vos données.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={() => setShowCgu(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BecomeCreatorPage;
